/**
 * General MIDI Level 1 — таблица мелодических программ (programs 0..127)
 * и хелпер `getInstrumentMeta(program, channel)`, который резолвит
 * программу + канал в `{family, name, percussion}`.
 *
 * Эта таблица — единственный источник правды для GM-имён, которые
 * парсер записывает в `MidiTrack.instrument` при обработке
 * Program Change (`0xc0`). Поведение:
 * - `channel === DRUM_CHANNEL` → возвращается
 *   `{family: "percussive", name: "standard kit", percussion: true}`,
 *   независимо от program (как и положено по GM Level 1: program
 *   number на канале 9 выбирает один из kit-вариантов, но без
 *   bank-select CC0/CC32 мы не различаем их — `percussion: true`
 *   достаточно для UX/трансформа).
 * - Иначе: `GM_MELODIC[program & 0x7f]` (clamp по диапазону
 *   `0..127`), `percussion: false`.
 *
 * Источник имён: General MIDI Level 1 (Roland GS-совместимая
 * подмножина). Имена и семейства в нижнем регистре, чтобы
 * UI мог делать capitalize при необходимости.
 */

import type { MidiChannel } from "./types.js";
import { DRUM_CHANNEL } from "./types.js";

export type InstrumentMeta = {
  family: string;
  name: string;
  percussion: boolean;
};

export const GM_FAMILIES = [
  "piano",
  "chromatic percussion",
  "organ",
  "guitar",
  "bass",
  "strings",
  "ensemble",
  "brass",
  "reed",
  "pipe",
  "synth lead",
  "synth pad",
  "synth effects",
  "ethnic",
  "percussive",
  "sound effects",
] as const;

/** GM Level 1 — 128 мелодических программ (для каналов ≠ 9). */
export const GM_MELODIC: ReadonlyArray<{ family: string; name: string }> = [
  { family: "piano", name: "acoustic grand piano" },
  { family: "piano", name: "bright acoustic piano" },
  { family: "piano", name: "electric grand piano" },
  { family: "piano", name: "honky-tonk piano" },
  { family: "piano", name: "electric piano 1" },
  { family: "piano", name: "electric piano 2" },
  { family: "piano", name: "harpsichord" },
  { family: "piano", name: "clavinet" },
  { family: "chromatic percussion", name: "celesta" },
  { family: "chromatic percussion", name: "glockenspiel" },
  { family: "chromatic percussion", name: "music box" },
  { family: "chromatic percussion", name: "vibraphone" },
  { family: "chromatic percussion", name: "marimba" },
  { family: "chromatic percussion", name: "xylophone" },
  { family: "chromatic percussion", name: "tubular bells" },
  { family: "chromatic percussion", name: "dulcimer" },
  { family: "organ", name: "drawbar organ" },
  { family: "organ", name: "percussive organ" },
  { family: "organ", name: "rock organ" },
  { family: "organ", name: "church organ" },
  { family: "organ", name: "reed organ" },
  { family: "organ", name: "accordion" },
  { family: "organ", name: "harmonica" },
  { family: "organ", name: "tango accordion" },
  { family: "guitar", name: "acoustic guitar (nylon)" },
  { family: "guitar", name: "acoustic guitar (steel)" },
  { family: "guitar", name: "electric guitar (jazz)" },
  { family: "guitar", name: "electric guitar (clean)" },
  { family: "guitar", name: "electric guitar (muted)" },
  { family: "guitar", name: "overdriven guitar" },
  { family: "guitar", name: "distortion guitar" },
  { family: "guitar", name: "guitar harmonics" },
  { family: "bass", name: "acoustic bass" },
  { family: "bass", name: "electric bass (finger)" },
  { family: "bass", name: "electric bass (pick)" },
  { family: "bass", name: "fretless bass" },
  { family: "bass", name: "slap bass 1" },
  { family: "bass", name: "slap bass 2" },
  { family: "bass", name: "synth bass 1" },
  { family: "bass", name: "synth bass 2" },
  { family: "strings", name: "violin" },
  { family: "strings", name: "viola" },
  { family: "strings", name: "cello" },
  { family: "strings", name: "contrabass" },
  { family: "strings", name: "tremolo strings" },
  { family: "strings", name: "pizzicato strings" },
  { family: "strings", name: "orchestral harp" },
  { family: "strings", name: "timpani" },
  { family: "ensemble", name: "string ensemble 1" },
  { family: "ensemble", name: "string ensemble 2" },
  { family: "ensemble", name: "synth strings 1" },
  { family: "ensemble", name: "synth strings 2" },
  { family: "ensemble", name: "choir aahs" },
  { family: "ensemble", name: "voice oohs" },
  { family: "ensemble", name: "synth choir" },
  { family: "ensemble", name: "orchestra hit" },
  { family: "brass", name: "trumpet" },
  { family: "brass", name: "trombone" },
  { family: "brass", name: "tuba" },
  { family: "brass", name: "muted trumpet" },
  { family: "brass", name: "french horn" },
  { family: "brass", name: "brass section" },
  { family: "brass", name: "synth brass 1" },
  { family: "brass", name: "synth brass 2" },
  { family: "reed", name: "soprano sax" },
  { family: "reed", name: "alto sax" },
  { family: "reed", name: "tenor sax" },
  { family: "reed", name: "baritone sax" },
  { family: "reed", name: "oboe" },
  { family: "reed", name: "english horn" },
  { family: "reed", name: "bassoon" },
  { family: "reed", name: "clarinet" },
  { family: "pipe", name: "piccolo" },
  { family: "pipe", name: "flute" },
  { family: "pipe", name: "recorder" },
  { family: "pipe", name: "pan flute" },
  { family: "pipe", name: "blown bottle" },
  { family: "pipe", name: "shakuhachi" },
  { family: "pipe", name: "whistle" },
  { family: "pipe", name: "ocarina" },
  { family: "synth lead", name: "lead 1 (square)" },
  { family: "synth lead", name: "lead 2 (sawtooth)" },
  { family: "synth lead", name: "lead 3 (calliope)" },
  { family: "synth lead", name: "lead 4 (chiffer)" },
  { family: "synth lead", name: "lead 5 (charang)" },
  { family: "synth lead", name: "lead 6 (voice)" },
  { family: "synth lead", name: "lead 7 (fifths)" },
  { family: "synth lead", name: "lead 8 (bass + lead)" },
  { family: "synth pad", name: "pad 1 (new age)" },
  { family: "synth pad", name: "pad 2 (warm)" },
  { family: "synth pad", name: "pad 3 (polysynth)" },
  { family: "synth pad", name: "pad 4 (choir)" },
  { family: "synth pad", name: "pad 5 (bowed)" },
  { family: "synth pad", name: "pad 6 (metallic)" },
  { family: "synth pad", name: "pad 7 (halo)" },
  { family: "synth pad", name: "pad 8 (sweep)" },
  { family: "synth effects", name: "fx 1 (rain)" },
  { family: "synth effects", name: "fx 2 (soundtrack)" },
  { family: "synth effects", name: "fx 3 (crystal)" },
  { family: "synth effects", name: "fx 4 (atmosphere)" },
  { family: "synth effects", name: "fx 5 (brightness)" },
  { family: "synth effects", name: "fx 6 (goblins)" },
  { family: "synth effects", name: "fx 7 (echoes)" },
  { family: "synth effects", name: "fx 8 (sci-fi)" },
  { family: "ethnic", name: "sitar" },
  { family: "ethnic", name: "banjo" },
  { family: "ethnic", name: "shamisen" },
  { family: "ethnic", name: "koto" },
  { family: "ethnic", name: "kalimba" },
  { family: "ethnic", name: "bagpipe" },
  { family: "ethnic", name: "fiddle" },
  { family: "ethnic", name: "shanai" },
  { family: "percussive", name: "tinkle bell" },
  { family: "percussive", name: "agogo" },
  { family: "percussive", name: "steel drums" },
  { family: "percussive", name: "woodblock" },
  { family: "percussive", name: "taiko drum" },
  { family: "percussive", name: "melodic tom" },
  { family: "percussive", name: "synth drum" },
  { family: "percussive", name: "reverse cymbal" },
  { family: "sound effects", name: "guitar fret noise" },
  { family: "sound effects", name: "breath noise" },
  { family: "sound effects", name: "seashore" },
  { family: "sound effects", name: "bird tweet" },
  { family: "sound effects", name: "telephone ring" },
  { family: "sound effects", name: "helicopter" },
  { family: "sound effects", name: "applause" },
  { family: "sound effects", name: "gunshot" },
];

/**
 * Возвращает `{family, name, percussion}` для данной пары
 * `(program, channel)`. На канале 9 (DRUM_CHANNEL) всегда
 * возвращается `standard kit / percussive` — без bank-select CC0/CC32
 * мы не различаем kit-варианты GM Level 2 / GS. Для остальных каналов
 * `program` клампится в `0..127` и индексирует `GM_MELODIC`.
 */
export const getInstrumentMeta = (program: number, channel: MidiChannel): InstrumentMeta => {
  if (channel === DRUM_CHANNEL) {
    return { family: "percussive", name: "standard kit", percussion: true };
  }
  const idx = Math.max(0, Math.min(127, program | 0));
  const entry = GM_MELODIC[idx]!;
  return { family: entry.family, name: entry.name, percussion: false };
};
